import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, Injector, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { KundeTokenService, StorageService } from '@comparit/infrastructure';
import { SnackbarService } from '@comparit/core';
import { FunnelLoginService } from '../../../services/funnel-login.service';
import { FormsService } from '../../../services/forms.service';
import { KfzRoutes } from '../../app.routes.paths';
import { mlpEnvironment } from './mlp-environment';
import { mlpPortalDashboardUrl } from './mlp-portal.helper';
import { MlpCallbackContextService } from './mlp-callback-context.service';

// Browsers clamp popups to roughly 100x100, so anything smaller is silently ignored.
const LOGOUT_WINDOW_SIZE = 100;
// Closing aborts the navigation, so this is a latency budget for both logout hops, not a cosmetic delay.
const LOGOUT_WINDOW_LIFETIME_MS = 2_000;

function logoutWindowFeatures(): string {
  const left = Math.max(0, screen.availWidth - LOGOUT_WINDOW_SIZE);
  const top = Math.max(0, screen.availHeight - LOGOUT_WINDOW_SIZE);

  return `popup,width=${LOGOUT_WINDOW_SIZE},height=${LOGOUT_WINDOW_SIZE},left=${left},top=${top}`;
}

@Component({
  selector: 'app-mlp-header',
  imports: [MatButtonModule, MatIconModule, MatMenuModule],
  templateUrl: './mlp-header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex min-w-0' },
})
export class MlpHeaderComponent {
  private readonly kundeLogin = inject(FunnelLoginService);
  private readonly kundeToken = inject(KundeTokenService);
  private readonly injector = inject(Injector);
  private readonly storage = inject(StorageService);
  private readonly snackbar = inject(SnackbarService);
  private readonly callbackContext = inject(MlpCallbackContextService);
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  protected readonly windowMode = this.kundeToken.windowMode;
  protected readonly isLoggedIn = this.kundeToken.isLoggedIn;
  protected readonly displayName = this.kundeToken.displayName;
  protected readonly portalDashboardUrl = mlpPortalDashboardUrl;
  protected readonly loggingOut = this.kundeLogin.loggingOut;

  protected login(): void {
    this.kundeLogin.openLogin();
  }

  protected logout(): void {
    this.loggingOut.set(true);
    this.kundeToken.logout();
    this.injector.get(FormsService).resetAll();
    this.storage.clearAll();

    // resetAll() only touches form groups, so the callback latch would otherwise survive into the
    // next customer's funnel on this tab and show the link from page 1.
    this.callbackContext.reset();

    // The portal sends its session cookie only on a top-level navigation, so an iframe or fetch logs nobody out.
    const portalWindow = window.open(mlpEnvironment.logoutUrl, '_blank', logoutWindowFeatures());

    if (!portalWindow) {
      // The forms are already empty, so leave the page behind the spinner (e.g. the results) before
      // showing it again. A router navigation, not a reload, so the retry toast can stay — but only
      // after navigating: SnackbarService closes all open alerts on NavigationStart.
      void this.router.navigateByUrl(KfzRoutes.formular, { replaceUrl: true }).then(() => {
        this.loggingOut.set(false);
        this.snackbar.warning('Die Abmeldung im Kundenportal wurde von deinem Browser blockiert.', {
          action: {
            label: 'Abmelden',
            callback: () => window.open(mlpEnvironment.logoutUrl, '_blank'),
            value: null,
          },
        });
      });
      return;
    }

    window.focus();

    setTimeout(() => {
      portalWindow.close();
      this.document.location.replace(`${KfzRoutes.logout}?branding=mlp`);
    }, LOGOUT_WINDOW_LIFETIME_MS);
  }
}
